import { motion } from "framer-motion";
import { ArrowRight, MessagesSquare, Puzzle, Baby, School } from "lucide-react";
import { Reveal } from "./Reveal";
import { prefillMotivo } from "../config/site";

const pillars = [
  {
    icon: MessagesSquare,
    title: "Lenguaje y comunicación",
    desc: "Primeras palabras, comprensión y comunicación en el día a día.",
  },
  {
    icon: Puzzle,
    title: "Juego y atención",
    desc: "El juego como ventana al desarrollo: exploración, atención compartida y curiosidad.",
  },
  {
    icon: Baby,
    title: "Autonomía y rutinas",
    desc: "Pequeñas independencias que dan seguridad y confianza en casa y fuera de ella.",
  },
  {
    icon: School,
    title: "Familia y escuela infantil",
    desc: "Pautas prácticas para el hogar y coordinación con la escuela infantil cuando hace falta.",
  },
];

export default function EarlyYears() {
  return (
    <section id="desarrollo-0-6" data-testid="early-years-section" className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-sage-light/50 border border-sage/20 rounded-[2.5rem] p-7 sm:p-12 lg:p-16 relative overflow-hidden">
          <div
            aria-hidden="true"
            className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-wood/30 blur-3xl"
          />
          <div className="relative grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <div className="lg:col-span-5">
              <Reveal>
                <div className="relative max-w-md mx-auto lg:mx-0">
                  <div
                    aria-hidden="true"
                    className="absolute -top-5 -left-5 w-full h-full rounded-t-[8rem] rounded-b-[2rem] bg-wood/40"
                  />
                  <div className="relative overflow-hidden rounded-t-[8rem] rounded-b-[2rem] shadow-[0_20px_50px_rgba(27,59,111,0.12)] aspect-[4/5]">
                    <img
                      src="https://images.pexels.com/photos/6693310/pexels-photo-6693310.jpeg?auto=format&fit=crop&w=900&q=85"
                      alt="Mano de un niño pequeño jugando con bloques de colores sobre una alfombra, sin rostro visible"
                      data-testid="early-years-image"
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <motion.div
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7, delay: 0.3 }}
                    className="absolute -bottom-6 left-6 right-6 sm:right-auto bg-white/85 backdrop-blur-xl border border-atento-blue/10 rounded-2xl px-6 py-4 shadow-lg text-center"
                    data-testid="early-years-card"
                  >
                    <p className="font-serif italic text-lg text-atento-blue leading-tight">
                      Observar a tiempo es acompañar mejor
                    </p>
                  </motion.div>
                </div>
              </Reveal>
            </div>

            <div className="lg:col-span-7">
              <Reveal>
                <p className="eyebrow" data-testid="early-years-eyebrow">
                  Acompañamiento al desarrollo · 0-6 años
                </p>
                <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-[2.75rem] font-semibold tracking-tight leading-[1.12] text-atento-blue">
                  Los primeros años cuentan, y mucho
                </h2>
              </Reveal>
              <Reveal delay={0.12} className="mt-6 space-y-5 text-base sm:text-lg leading-relaxed text-charcoal/75">
                <p>
                  Entre los 0 y los 6 años el desarrollo avanza a un ritmo enorme, y cada niño y
                  cada niña tiene el suyo. A veces surgen dudas: si habla poco, si le cuesta
                  atender, si no juega como otros peques. Observar a tiempo y con mirada
                  profesional permite acompañar el desarrollo con tranquilidad, sin alarmas y
                  sin esperar a que las dificultades crezcan.
                </p>
                <p>
                  En el gabinete observo el desarrollo a través del juego y la relación, y trabajo
                  codo a codo con la familia: pautas claras para casa y coordinación con la
                  escuela infantil cuando es necesario.
                </p>
              </Reveal>

              <div className="mt-9 grid sm:grid-cols-2 gap-4">
                {pillars.map((p, i) => (
                  <Reveal key={p.title} delay={0.15 + i * 0.08}>
                    <div
                      data-testid={`early-years-pillar-${i}`}
                      className="bg-white/80 border border-atento-blue/8 rounded-2xl p-5 h-full hover:border-sage/40 transition-colors duration-300"
                    >
                      <p.icon className="w-5 h-5 text-sage-dark" strokeWidth={1.8} />
                      <h3 className="mt-3 font-accent font-semibold text-atento-blue text-base leading-snug">
                        {p.title}
                      </h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-charcoal/65">{p.desc}</p>
                    </div>
                  </Reveal>
                ))}
              </div>

              <Reveal delay={0.3}>
                <button
                  onClick={() => prefillMotivo("Hola Lidia, me gustaría recibir información sobre el acompañamiento al desarrollo (0-6 años).")}
                  data-testid="early-years-cta"
                  className="mt-9 inline-flex items-center justify-center gap-2 bg-atento-blue hover:bg-atento-dark text-white font-accent font-semibold px-8 py-4 rounded-full shadow-md hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5 cursor-pointer"
                >
                  Cuéntame cómo es vuestro peque
                  <ArrowRight className="w-4 h-4" />
                </button>
              </Reveal>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
